(() => {
  const LOADER_ID = 'hs-script-loader';
  const LOADER_SRC = 'https://js.hs-scripts.com/39823395.js';
  const EMBED_SRC = '//js.hsforms.net/forms/embed/v2.js';
  const TERMLY_WAIT_MS = 10000;
  const TERMLY_CACHE_KEY = 'TERMLY_API_CACHE';
  const TERMLY_CONSENT_KEY = 'TERMLY_COOKIE_CONSENT';
  const CREATE_RE = /hbspt\s*\.\s*forms\s*\.\s*create/;

  let trackingLoaded = false;
  let embedsActivated = false;
  let embedsLoading = false;
  let lastTrackingAllowed = null;
  let lastEmbedAllowed = null;
  let termlyConsentMode = null;
  let termlyEventDecision = null;
  const createdFormKeys = new Set();

  const isDesignMode = () => document.documentElement.classList.contains('shopify-design-mode');

  const termlyExpected = () =>
    Boolean(window.Termly || document.querySelector('script[src*="app.termly.io"]'));

  // Termly.getConsentState() is getConsentSettings() || getDefaultConsents().
  // In opt_out regions the defaults are category=true (implied grant) before any click.
  const termlyStoredConsents = () => {
    try {
      const raw = window.localStorage.getItem(TERMLY_CACHE_KEY);
      if (!raw) return null;
      const entry = JSON.parse(raw)[TERMLY_CONSENT_KEY];
      if (!entry || entry.value == null || typeof entry.value !== 'object') return null;
      return entry.value;
    } catch (error) {
      return null;
    }
  };

  const categoriesToState = (categories) => {
    if (!Array.isArray(categories)) return null;
    const set = new Set(categories.map((name) => String(name)));
    return {
      essential: set.has('essential'),
      analytics: set.has('analytics'),
      advertising: set.has('advertising'),
      marketing: set.has('marketing'),
      performance: set.has('performance'),
      social_networking: set.has('social_networking'),
      unclassified: set.has('unclassified'),
    };
  };

  const termlyHasExplicitDecision = () => {
    if (termlyEventDecision && typeof termlyEventDecision === 'object') return true;
    return termlyStoredConsents() != null;
  };

  const rememberTermlyDecision = (data) => {
    if (!data || typeof data !== 'object') return;
    // Automatic first-load `consent` events carry `categories` from regional
    // opt_out defaults while the banner is still showing and nothing is stored.
    // Those are not a visitor decision. Official Accept/Decline payloads may
    // include consentState; categories-only counts only after Termly persists.
    if (data.consentState && typeof data.consentState === 'object') {
      termlyEventDecision = data.consentState;
      return;
    }
    if (!termlyStoredConsents()) return;
    const fromCategories = categoriesToState(data.categories);
    if (fromCategories) termlyEventDecision = fromCategories;
  };

  const termlyMarketingGranted = (state) =>
    Boolean(state && (state.advertising === true || state.marketing === true));

  const termlyState = () => {
    if (!termlyExpected()) return null;
    if (!window.Termly || typeof window.Termly.getConsentState !== 'function') {
      return {
        ready: false,
        analytics: false,
        marketing: false,
        explicit: termlyHasExplicitDecision(),
        consentMode: termlyConsentMode,
      };
    }
    const state = window.Termly.getConsentState() || {};
    return {
      ready: true,
      analytics: state.analytics === true,
      marketing: termlyMarketingGranted(state),
      explicit: termlyHasExplicitDecision(),
      consentMode: termlyConsentMode,
    };
  };

  const shopifyState = () => {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return null;
    return {
      ready: true,
      analytics: privacy.analyticsProcessingAllowed() === true,
      marketing: typeof privacy.marketingAllowed === 'function' ? privacy.marketingAllowed() === true : false,
      preferences:
        typeof privacy.preferencesProcessingAllowed === 'function'
          ? privacy.preferencesProcessingAllowed() === true
          : null,
      saleOfData: typeof privacy.saleOfDataAllowed === 'function' ? privacy.saleOfDataAllowed() === true : true,
      currentVisitorConsent:
        typeof privacy.currentVisitorConsent === 'function' ? privacy.currentVisitorConsent() : null,
    };
  };

  // Tracking (scriptloader / hs-scripts / analytics / collected forms): same rules as PR #137.
  const hubspotTrackingAllowed = () => {
    if (isDesignMode()) return true;

    const termly = termlyState();
    const shopify = shopifyState();

    if (termly && termly.ready === false) return false;
    if (!termly && !shopify) return false;

    // GPC / do-not-sell when Shopify surfaces it — HubSpot tracking is third-party.
    if (shopify && shopify.saleOfData === false) return false;

    // Decline / GPC: categories false means HubSpot tracking stays off.
    if (termly && termly.ready && !termly.analytics && !termly.marketing) return false;
    if (shopify && !shopify.analytics && !shopify.marketing) return false;

    // Termly present: implied opt_out defaults (category=true, no decision yet) are not consent.
    if (termly) {
      if (!termly.ready || !termly.explicit) return false;
      return Boolean(termly.analytics || termly.marketing);
    }

    return Boolean(shopify && (shopify.analytics || shopify.marketing));
  };

  // Forms only need an explicit Termly Accept. Shopify saleOfData / GPC must not blank the embed.
  const hubspotEmbedAllowed = () => {
    if (isDesignMode()) return true;

    const termly = termlyState();
    const shopify = shopifyState();

    if (termly && termly.ready === false) return false;
    if (!termly && !shopify) return false;

    if (termly && termly.ready && !termly.analytics && !termly.marketing) return false;

    if (termly) {
      if (!termly.ready || !termly.explicit) return false;
      return Boolean(termly.analytics || termly.marketing);
    }

    return Boolean(shopify && (shopify.analytics || shopify.marketing));
  };

  const hubspotAllowed = hubspotTrackingAllowed;

  const formQueue = () => {
    window.__tileclubHbsptQueue = window.__tileclubHbsptQueue || [];
    return window.__tileclubHbsptQueue;
  };

  const formKey = (opts) => {
    if (!opts || typeof opts !== 'object') return String(opts);
    return [opts.portalId || '', opts.formId || '', opts.target || ''].join(':');
  };

  const isRealHbspt = () =>
    Boolean(
      window.hbspt &&
        window.hbspt.forms &&
        typeof window.hbspt.forms.create === 'function' &&
        !window.hbspt.forms.create.__tileclubStub
    );

  const installHbsptStub = () => {
    if (isRealHbspt()) return;
    if (window.hbspt && window.hbspt.forms && window.hbspt.forms.create && window.hbspt.forms.create.__tileclubStub) {
      return;
    }
    const stubCreate = (opts) => {
      formQueue().push(opts);
    };
    stubCreate.__tileclubStub = true;
    window.hbspt = {
      __tileclubStub: true,
      forms: {
        create: stubCreate,
      },
    };
  };

  const clearHbsptStub = () => {
    if (window.hbspt && window.hbspt.forms && window.hbspt.forms.create && window.hbspt.forms.create.__tileclubStub) {
      delete window.hbspt;
    }
  };

  const wrapRealCreate = () => {
    if (!isRealHbspt() || window.hbspt.forms.__tileclubWrapped) return;
    const real = window.hbspt.forms.create.bind(window.hbspt.forms);
    window.hbspt.forms.create = (opts) => {
      const key = formKey(opts);
      if (key && createdFormKeys.has(key)) return;
      if (key) createdFormKeys.add(key);
      return real(opts);
    };
    window.hbspt.forms.__tileclubWrapped = true;
  };

  const flushFormQueue = () => {
    if (!isRealHbspt()) return;
    wrapRealCreate();
    const queue = formQueue();
    while (queue.length) {
      window.hbspt.forms.create(queue.shift());
    }
  };

  let hbsptReadyTimer = null;
  let storedConsentWatch = null;

  const stopHbsptReadyWatch = () => {
    if (!hbsptReadyTimer) return;
    clearInterval(hbsptReadyTimer);
    hbsptReadyTimer = null;
  };

  const finishEmbeds = () => {
    if (!hubspotEmbedAllowed()) {
      stopHbsptReadyWatch();
      embedsLoading = false;
      return false;
    }
    if (!isRealHbspt()) return false;
    wrapRealCreate();
    flushFormQueue();
    pendingEmbedNodes()
      .filter((node) => !node.getAttribute('src') && !node.getAttribute('data-src'))
      .forEach((node) => activateScript(node));
    flushFormQueue();
    embedsActivated = true;
    embedsLoading = false;
    stopHbsptReadyWatch();
    return true;
  };

  const startHbsptReadyWatch = () => {
    if (finishEmbeds()) return;
    if (hbsptReadyTimer) return;
    const started = Date.now();
    let retried = false;
    hbsptReadyTimer = setInterval(() => {
      if (finishEmbeds()) return;
      if (!retried && Date.now() - started > 2000 && !isRealHbspt()) {
        retried = true;
        if (!document.querySelector('script[src*="hsforms.net"][data-hubspot-consent="allow"]')) {
          ensureEmbedLibrary();
        }
      }
    }, 50);
  };

  const watchStoredConsent = () => {
    if (storedConsentWatch || termlyHasExplicitDecision()) return;
    const started = Date.now();
    storedConsentWatch = setInterval(() => {
      if (termlyStoredConsents()) {
        clearInterval(storedConsentWatch);
        storedConsentWatch = null;
        syncHubspot();
        return;
      }
      if (Date.now() - started > 8000) {
        clearInterval(storedConsentWatch);
        storedConsentWatch = null;
      }
    }, 50);
  };

  const ensureHubspotQueues = () => {
    window._hsq = window._hsq || [];
    window._hsp = window._hsp || [];
    window.disableHubSpotCookieBanner = true;
  };

  const loadTracking = () => {
    if (trackingLoaded || document.getElementById(LOADER_ID)) {
      trackingLoaded = true;
      return;
    }
    ensureHubspotQueues();
    window._hsq.push(['doNotTrack', { track: true }]);
    const script = document.createElement('script');
    script.id = LOADER_ID;
    script.src = LOADER_SRC;
    script.defer = true;
    script.dataset.hubspotConsent = 'allow';
    document.head.appendChild(script);
    trackingLoaded = true;
  };

  const activateScript = (source, onSettled) => {
    if (source.dataset.hubspotActivated === 'true') {
      if (typeof onSettled === 'function') onSettled();
      return null;
    }
    source.dataset.hubspotActivated = 'true';
    const script = document.createElement('script');
    const src = source.getAttribute('src') || source.getAttribute('data-src');
    const charset = source.getAttribute('charset');
    if (charset) script.charset = charset;
    script.dataset.hubspotConsent = 'allow';

    let settled = false;
    const done = () => {
      if (settled || typeof onSettled !== 'function') return;
      settled = true;
      onSettled();
    };

    // Listen before setting src / inserting so a cached v2.js cannot fire load first.
    if (src) {
      script.addEventListener('load', done, { once: true });
      script.addEventListener('error', done, { once: true });
      script.src = src;
    } else {
      script.text = source.textContent || '';
    }
    source.parentNode.insertBefore(script, source.nextSibling);
    if (!src || script.readyState === 'complete' || script.readyState === 'loaded') done();
    return script;
  };

  const pendingEmbedNodes = () => {
    const nodes = Array.from(
      document.querySelectorAll(
        'script[data-hubspot-consent="embed"], script[src*="hsforms.net"][type="text/plain"], script[data-src*="hsforms.net"][type="text/plain"]'
      )
    );
    return nodes.filter((node) => node.dataset.hubspotActivated !== 'true');
  };

  const ensureEmbedLibrary = () => {
    if (isRealHbspt()) {
      startHbsptReadyWatch();
      return;
    }
    if (document.querySelector('script[src*="hsforms.net"][data-hubspot-consent="allow"]')) {
      startHbsptReadyWatch();
      return;
    }
    const existing = document.querySelector('script[src*="hsforms.net"]:not([type="text/plain"])');
    if (existing && !(window.hbspt && window.hbspt.__tileclubStub)) {
      startHbsptReadyWatch();
      return;
    }
    clearHbsptStub();
    const script = document.createElement('script');
    script.charset = 'utf-8';
    script.dataset.hubspotConsent = 'allow';
    script.addEventListener('load', startHbsptReadyWatch, { once: true });
    script.addEventListener('error', startHbsptReadyWatch, { once: true });
    script.src = EMBED_SRC;
    document.head.appendChild(script);
    startHbsptReadyWatch();
    if (script.readyState === 'complete' || script.readyState === 'loaded') startHbsptReadyWatch();
  };

  const activateEmbeds = () => {
    if (!hubspotEmbedAllowed()) return;
    if (finishEmbeds()) return;

    const pending = pendingEmbedNodes();
    const withSrc = pending.filter((node) => node.getAttribute('src') || node.getAttribute('data-src'));

    if (withSrc.length) {
      clearHbsptStub();
      withSrc.forEach((node) => activateScript(node, startHbsptReadyWatch));
      embedsLoading = true;
      startHbsptReadyWatch();
      return;
    }

    embedsLoading = true;
    ensureEmbedLibrary();
  };

  const clearHubspotCookies = () => {
    const host = window.location.hostname;
    const root = host.split('.').slice(-2).join('.');
    const domains = [host, '.' + host, root, '.' + root, ''];

    document.cookie.split(';').forEach((part) => {
      const name = part.split('=')[0].trim();
      if (!name || !/^(hubspotutk|messagesUtk|__hs)/i.test(name)) return;
      if (name === '__hs_do_not_track') return;
      domains.forEach((domain) => {
        const domainAttr = domain ? '; domain=' + domain : '';
        document.cookie = name + '=; Max-Age=0; path=/' + domainAttr;
      });
    });
  };

  const HUBSPOT_LOADER_RE =
    /hubapi\.com\/scriptloader|hs-scripts\.com|hs-analytics\.net|js\.hscollectedforms\.net/i;

  const isUnauthorizedHubspotLoader = (node) => {
    if (!node || node.nodeName !== 'SCRIPT') return false;
    if (node.dataset.hubspotConsent === 'allow') return false;
    const src = node.getAttribute('src') || '';
    return node.id === LOADER_ID || HUBSPOT_LOADER_RE.test(src);
  };

  const neutralizePendingEmbeds = () => {
    document.querySelectorAll('script[src*="hsforms.net"]').forEach((script) => {
      if (script.dataset.hubspotConsent === 'allow') return;
      const src = script.getAttribute('src');
      if (src) script.setAttribute('data-src', src);
      script.removeAttribute('src');
      script.type = 'text/plain';
      if (!script.getAttribute('data-hubspot-consent')) {
        script.setAttribute('data-hubspot-consent', 'embed');
      }
    });
    document.querySelectorAll('script').forEach((script) => {
      if (script.dataset.hubspotConsent === 'allow') return;
      if (script.getAttribute('src') || script.getAttribute('data-src')) return;
      if (!CREATE_RE.test(script.textContent || '')) return;
      script.type = 'text/plain';
      if (!script.getAttribute('data-hubspot-consent')) {
        script.setAttribute('data-hubspot-consent', 'embed');
      }
    });
  };

  const removeTrackingScripts = () => {
    document
      .querySelectorAll(
        '#' +
          LOADER_ID +
          ', script[src*="hs-scripts.com"], script[src*="hs-analytics.net"], script[src*="js.hscollectedforms.net"], script[src*="hubapi.com/scriptloader"]'
      )
      .forEach((script) => {
        if (script.dataset.hubspotConsent === 'allow' && hubspotTrackingAllowed()) return;
        script.remove();
      });
    trackingLoaded = false;
  };

  let loaderGuard = null;
  const startLoaderGuard = () => {
    if (loaderGuard) return;
    loaderGuard = new MutationObserver((records) => {
      if (hubspotTrackingAllowed()) return;
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (isUnauthorizedHubspotLoader(node)) {
            node.remove();
            return;
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('script').forEach((script) => {
              if (isUnauthorizedHubspotLoader(script)) script.remove();
            });
          }
        });
      });
    });
    loaderGuard.observe(document.documentElement, { childList: true, subtree: true });
  };

  const stopLoaderGuard = () => {
    if (loaderGuard) {
      loaderGuard.disconnect();
      loaderGuard = null;
    }
    if (window.__tileclubHubspotEarlyGuard && typeof window.__tileclubHubspotEarlyGuard.disconnect === 'function') {
      window.__tileclubHubspotEarlyGuard.disconnect();
      window.__tileclubHubspotEarlyGuard = null;
    }
  };

  const disableTracking = () => {
    ensureHubspotQueues();
    try {
      window._hsp.push(['revokeCookieConsent']);
    } catch (error) {
      // HubSpot may not have loaded yet.
    }
    try {
      window._hsq.push(['doNotTrack']);
    } catch (error) {
      // HubSpot may not have loaded yet.
    }
    removeTrackingScripts();
    startLoaderGuard();
    clearHubspotCookies();
  };

  const syncHubspot = () => {
    const embeds = hubspotEmbedAllowed();
    const tracking = hubspotTrackingAllowed();

    if (embeds !== lastEmbedAllowed) {
      lastEmbedAllowed = embeds;
      if (embeds) {
        activateEmbeds();
      } else {
        stopHbsptReadyWatch();
        embedsLoading = false;
        neutralizePendingEmbeds();
      }
    } else if (embeds) {
      activateEmbeds();
    }

    if (tracking === lastTrackingAllowed) return;
    lastTrackingAllowed = tracking;
    if (tracking) {
      stopLoaderGuard();
      loadTracking();
    } else {
      disableTracking();
    }
  };

  const listenTermly = () => {
    if (!window.Termly || typeof window.Termly.on !== 'function') return;
    window.Termly.on('initialized', (data) => {
      if (data && data.region_settings && data.region_settings.consent_mode) {
        termlyConsentMode = data.region_settings.consent_mode;
      }
      syncHubspot();
    });
    window.Termly.on('consent', (data) => {
      rememberTermlyDecision(data);
      syncHubspot();
      // Same-page Accept/Decline may persist TERMLY_COOKIE_CONSENT after the
      // event. Do not use getConsentState() here — it returns opt_out defaults.
      if (!termlyHasExplicitDecision()) watchStoredConsent();
    });
  };

  const whenTermlyReady = (callback) => {
    let firstSync = false;
    const runFirstSync = () => {
      if (firstSync) return;
      firstSync = true;
      callback();
    };

    const attach = () => {
      listenTermly();
      runFirstSync();
      syncHubspot();
      if (!termlyHasExplicitDecision()) watchStoredConsent();
    };

    if (window.Termly && typeof window.Termly.getConsentState === 'function') {
      attach();
      return;
    }

    if (!termlyExpected()) {
      runFirstSync();
      return;
    }

    const started = Date.now();
    const timer = setInterval(() => {
      if (window.Termly && typeof window.Termly.getConsentState === 'function') {
        clearInterval(timer);
        attach();
      } else if (Date.now() - started > TERMLY_WAIT_MS) {
        clearInterval(timer);
        runFirstSync();
      }
    }, 50);
  };

  const listenShopify = (callback) => {
    document.addEventListener('visitorConsentCollected', syncHubspot);

    if (window.Shopify && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function') {
      callback();
      return;
    }

    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      callback();
      return;
    }

    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {
      callback();
    });
  };

  window.__tileclubHubspotConsent = {
    allowed: hubspotTrackingAllowed,
    embedAllowed: hubspotEmbedAllowed,
    sync: syncHubspot,
    debug: () => {
      const termly = termlyState();
      const tracking = hubspotTrackingAllowed();
      const embeds = hubspotEmbedAllowed();
      return {
        termly,
        shopify: shopifyState(),
        storedConsents: termlyStoredConsents(),
        allowed: tracking,
        trackingAllowed: tracking,
        embedAllowed: embeds,
        trackingLoaded,
        embedsActivated,
        embedsLoading,
        realHbspt: isRealHbspt(),
        wrapperInstalled: Boolean(isRealHbspt() && window.hbspt.forms && window.hbspt.forms.__tileclubWrapped),
        queuedFormCreates: formQueue().length,
        customScripts: Array.isArray(window.custom_scripts) ? window.custom_scripts.slice() : [],
        hubspotLoaderCount: document.querySelectorAll(
          'script[src*="hubapi.com/scriptloader"], script[src*="hs-scripts.com"], script[src*="hs-analytics.net"], #hs-script-loader'
        ).length,
        hsformsExecutableCount: document.querySelectorAll(
          'script[src*="hsforms.net"]:not([type="text/plain"])'
        ).length,
        reason: !tracking && !embeds
          ? termly && termly.ready && !termly.explicit
            ? 'no-explicit-termly-decision'
            : 'not-allowed'
          : embeds && !tracking
            ? 'embeds-allowed-tracking-denied'
            : trackingLoaded
              ? 'allowed-and-loaded'
              : 'allowed',
      };
    },
  };

  if (Array.isArray(window.custom_scripts)) {
    window.custom_scripts = window.custom_scripts.filter(
      (url) => !/hubapi\.com\/scriptloader|hs-scripts\.com|hs-analytics\.net/i.test(String(url))
    );
  }
  installHbsptStub();
  neutralizePendingEmbeds();
  startLoaderGuard();

  whenTermlyReady(() => {
    listenShopify(syncHubspot);
  });
})();
